import { useEffect, useState } from 'react'
import { BarChart3, Loader2, PlugZap, Save, CheckCircle2, XCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  useConfiguracionAnalyticsDifare,
  useGuardarConfiguracionAnalyticsDifare,
  useTestConnectionAnalyticsDifare,
} from '../hooks/useAnalyticsDifare'
import type { TestConnectionResult } from '../types'

export function ConfiguracionAnalyticsDifarePage() {
  const { data, isLoading, error } = useConfiguracionAnalyticsDifare()
  const guardarMut = useGuardarConfiguracionAnalyticsDifare()
  const testMut = useTestConnectionAnalyticsDifare()

  const [apiBaseUrl, setApiBaseUrl] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [activo, setActivo] = useState(true)
  const [saved, setSaved] = useState(false)
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null)

  // Rellena el form con la config guardada (la key llega enmascarada, no editable
  // salvo que el usuario escriba una nueva).
  useEffect(() => {
    if (data) {
      setApiBaseUrl(data.api_base_url ?? '')
      setActivo(data.activo)
    }
  }, [data])

  const handleSave = async () => {
    setSaved(false)
    setTestResult(null)
    await guardarMut.mutateAsync({
      api_base_url: apiBaseUrl.trim(),
      // Solo enviamos la key si el usuario escribió una nueva.
      api_key: apiKey.trim() ? apiKey.trim() : undefined,
      activo,
    })
    setApiKey('')
    setSaved(true)
  }

  const handleTest = async () => {
    setSaved(false)
    // Prueba con los valores del formulario (sin necesidad de guardar primero).
    // La key solo se envía si el usuario escribió una nueva; si no, se usa la guardada.
    const result = await testMut.mutateAsync({
      api_base_url: apiBaseUrl.trim() || undefined,
      api_key: apiKey.trim() || undefined,
    })
    setTestResult(result)
  }

  // Se puede probar si hay host y una key (recién escrita o ya guardada).
  const puedeProbar = Boolean(apiBaseUrl.trim()) && (Boolean(apiKey.trim()) || Boolean(data?.tiene_api_key))

  const keyPlaceholder = data?.tiene_api_key
    ? `Guardada: ${data.api_key_masked} — escribe una nueva para reemplazarla`
    : 'Pega aquí la API key (X-API-Key / REPORTES_API_KEY)'

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center gap-3">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <BarChart3 className="h-6 w-6" />
          Configuración Difare Expertos
        </h1>
      </div>

      <p className="text-sm text-muted-foreground">
        Conexión al API de reportería de Difare Expertos. El consumo es
        server-to-server con el header <code className="font-mono">X-API-Key</code>.
      </p>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            No se pudo cargar la configuración. Reintenta o revisa tus permisos.
          </AlertDescription>
        </Alert>
      )}

      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-lg">Credenciales del API</CardTitle>
          <CardDescription>
            La API key se guarda cifrada; solo se muestra enmascarada.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando configuración…
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="api_base_url">URL base (host)</Label>
                <Input
                  id="api_base_url"
                  type="url"
                  placeholder="https://difare-expertos.tonalstrategies.com/api"
                  value={apiBaseUrl}
                  onChange={(e) => setApiBaseUrl(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Se consultará <code className="font-mono">{'{host}'}/analytics/overview</code>.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="api_key">API key</Label>
                <Input
                  id="api_key"
                  type="password"
                  autoComplete="off"
                  placeholder={keyPlaceholder}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                />
              </div>

              <div className="flex items-center justify-between rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2">
                <div>
                  <Label htmlFor="activo" className="cursor-pointer">Activo</Label>
                  <p className="text-xs text-muted-foreground">
                    Desactívalo para pausar el consumo del API.
                  </p>
                </div>
                <Switch id="activo" checked={activo} onCheckedChange={setActivo} />
              </div>

              {saved && (
                <Alert>
                  <AlertDescription className="flex items-center gap-2 text-emerald-600">
                    <CheckCircle2 className="h-4 w-4" /> Configuración guardada.
                  </AlertDescription>
                </Alert>
              )}

              {testResult && (
                <Alert variant={testResult.success ? 'default' : 'destructive'}>
                  <AlertDescription className="flex items-start gap-2">
                    {testResult.success ? (
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600" />
                    ) : (
                      <XCircle className="h-4 w-4 mt-0.5" />
                    )}
                    <span>
                      {testResult.mensaje}
                      {testResult.status_code ? ` (HTTP ${testResult.status_code})` : ''}
                      {testResult.detalle ? (
                        <span className="block text-xs opacity-80 mt-1">{testResult.detalle}</span>
                      ) : null}
                    </span>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Button
                  onClick={handleSave}
                  disabled={guardarMut.isPending || !apiBaseUrl.trim()}
                  className="bg-indigo-800 hover:bg-indigo-900 text-white"
                >
                  {guardarMut.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  Guardar
                </Button>
                <Button
                  variant="outline"
                  onClick={handleTest}
                  disabled={testMut.isPending || !puedeProbar}
                  title={!puedeProbar ? 'Ingresa el host y la API key para probar' : undefined}
                >
                  {testMut.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <PlugZap className="mr-2 h-4 w-4" />
                  )}
                  Probar conexión
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
